// M3 host-government fiscal take (M3_SPEC §8) and the US Windfall Profit Tax 1980-88 (§15), charged with each sale.
//   GOVERNMENT_TAKE regions replace M2's scheduledRoyalty (Saudi/Kuwait 0.5 gross was a stand-in for the fifty-fifty):
//     royalty  = terms.royalty × basis price  (basis 'posted' = tax reference price; 'realized' = what the cargo fetched)
//     special  = specialTax × max(0, margin)  on barrels above the field allowance (UK PRT, Norway special tax)
//     host tax = royaltyCredited ? max(royalty, incomeTax × margin) : royalty + incomeTax × (margin − royalty)
//   The royalty goes through production.royaltyRate (P&L `royalties`); the rest is P&L `hostTax`. Host income tax is
//   creditable against US tax on foreign income (m2/taxes.js reads fiscalMonths).
import { governmentTake, hostTakePerBbl, GOVERNMENT_TAKE } from '../../data/m3/regions.js';
import { WINDFALL_PROFIT_TAX, windfallActive, windfallTier, M3_FROM } from '../../data/m3/rules.js';
import { inflate } from '../../data/cpi.js';
import { dayToDate } from '../../core/time.js';
import { fieldDef, regionDef, yf } from '../defs.js';
import { spend, fmt$ } from '../util.js';
import { econState, bumpStat, producingWells, round2 } from './econ.js';
import { isUsRegion, postedOverRealized, wellFirstProdYear, usTier } from './prices.js';
import { wellNat, natStatus } from './nationalize.js';
import { m4TermsFor, m4TakePerBbl } from '../m4/marketFiscal.js'; // M4-B: GOVERNMENT_TAKE_M4, PSCs, Iraq TSC, federal offshore royalty (2000+)

export const LIFTING_COST_FLOOR = 0.1; // era $/bbl minimum lifting cost for the margin estimate

export function termsFor(state, fieldId) {
  const y = yf(state);
  if (y < M3_FROM) return null;
  const t4 = m4TermsFor(state, fieldId); // M4-B: undefined before 2000 / no M4 schedule → M3 terms
  if (t4 !== undefined) return t4;
  const rid = fieldDef(fieldId)?.regionId;
  return rid ? governmentTake(rid, y) : null;
}

// production.royaltyRate hook: effective royalty as a share of the realized gross, or null (use the M2 rate).
export function m3RoyaltyRate(state, fieldId) {
  const t = termsFor(state, fieldId);
  if (!t) return null;
  const rid = fieldDef(fieldId).regionId;
  const ratio = t.basis === 'posted' ? postedOverRealized(rid, yf(state)) : 1;
  return t.royalty * ratio;
}

// Field lifting cost per bbl today (era $): per-well opex over today's rate, + transport to market.
export function liftingCostPerBbl(state, fieldId, opexPerWell, nb) {
  const ws = producingWells(state, fieldId);
  const q = ws.reduce((a, w) => a + (w.prod.rate ?? 0), 0);
  const opex = q > 0 ? (ws.length * opexPerWell) / q : 0;
  return Math.max(LIFTING_COST_FLOOR, opex + (nb?.transport ?? 0) + (nb?.region ?? 0));
}

// Host take per barrel beyond the royalty, given realized gross per bbl.
export function hostTaxPerBbl(state, fieldId, grossPerBbl, costPerBbl) {
  const t = termsFor(state, fieldId);
  if (!t || !(grossPerBbl > 0)) return { extra: 0, royalty: 0, take: 0, posted: grossPerBbl, terms: t };
  const rid = fieldDef(fieldId).regionId;
  const posted = grossPerBbl * postedOverRealized(rid, yf(state));
  let terms = t;
  if ((t.specialTax ?? 0) > 0 && t.allowance) {
    const q = producingWells(state, fieldId).reduce((a, w) => a + (w.prod.rate ?? 0), 0);
    const frac = q > 0 ? Math.max(0, 1 - t.allowance / q) : 0;
    terms = { ...t, specialTax: t.specialTax * frac };
  }
  const take = (m4TakePerBbl(state, terms, grossPerBbl, costPerBbl) ?? hostTakePerBbl(terms, grossPerBbl, posted, costPerBbl)) + (t.exportDutyPerBbl ?? 0); // M4-B: PSC / TSC take
  const royalty = t.royalty * (t.basis === 'posted' ? posted : grossPerBbl);
  return { extra: Math.max(0, take - royalty), royalty, take, posted, terms };
}

// ---- Windfall Profit Tax ---------------------------------------------------------------------------
// "Independent" for depletion and the WPT (DEPLETION_M3.majorTest): refinery runs under 50,000 bbl/d (no retail in-game).
export const isIndependent = (state) => Object.values(state.refineries ?? {}).reduce((a, r) => a + (r.throughputBbl ?? 0), 0) < 50000;

export function companyUsBbld(state) {
  let q = 0;
  for (const w of Object.values(state.wells)) if (w.status === 'PRODUCING' && w.prod?.rate > 0 && !w.ownerOperatorId && isUsRegion(fieldDef(w.fieldId)?.regionId)) q += w.prod.rate;
  return q;
}

function wellWptTier(state, w) {
  const res = state.fields[w.fieldId]?.reservoirs?.[w.reservoirId];
  const heavy = (fieldDef(w.fieldId)?.reservoirs ?? []).find((r) => r.id === w.reservoirId)?.api < 16;
  return windfallTier({ completedYear: wellFirstProdYear(w) ?? 1970, bbld: w.prod?.rate ?? 100, tertiary: !!(res?.eor || res?.eorMethod), heavy: !!heavy });
}

// Per-barrel WPT for a field at a realized price (era $), blended over its wells.
export function windfallPerBbl(state, fieldId, price) {
  const y = yf(state);
  if (!windfallActive(y) || !isUsRegion(fieldDef(fieldId)?.regionId)) return 0;
  const W = WINDFALL_PROFIT_TAX;
  const indep = isIndependent(state);
  const usQ = companyUsBbld(state);
  const indepShare = indep && usQ > 0 ? Math.min(1, W.independentBbld / usQ) : 0;
  let a = 0, b = 0;
  for (const w of producingWells(state, fieldId)) {
    const q = w.prod.rate;
    if (!(q > 0)) continue;
    const tier = W.tiers[wellWptTier(state, w)];
    let rate = tier.rateSchedule ? tier.rateSchedule.reduce((v, [yy, r]) => (y >= yy ? r : v), tier.rate) : tier.rate;
    let irate = tier.independentRate ?? rate;
    if (tier.independentExemptFrom && y >= tier.independentExemptFrom) irate = 0;
    rate = rate * (1 - indepShare) + irate * indepShare;
    const base = inflate(tier.basePrice, W.baseYear, Math.min(y, 2025));
    a += q * rate * Math.max(0, price - base); b += q;
  }
  return b > 0 ? a / b : 0;
}

// ---- the sale hook -------------------------------------------------------------------------------------
// production.shipAndSell after the M2 onSale: host tax, windfall tax, foreign-income ledger, concession take.
export function chargeFiscal(state, fieldId, bbl, gross, nb, opexPerWell = 0) {
  if (!(gross > 0) || !(bbl > 0) || yf(state) < M3_FROM) return null;
  const rid = fieldDef(fieldId)?.regionId;
  const per = gross / bbl;
  const E = econState(state);
  const mk = monthKeyOf(state.day);
  const out = { hostTax: 0, windfall: 0 };
  if (rid && !isUsRegion(rid)) {
    const fm = (E.fiscalMonths[mk] ??= { foreignGross: 0, hostTax: 0 });
    fm.foreignGross += gross;
    const cost = liftingCostPerBbl(state, fieldId, opexPerWell, nb);
    const h = hostTaxPerBbl(state, fieldId, per, cost);
    if (h.extra > 0) {
      const amt = h.extra * bbl;
      spend(state, amt, 'hostTax', false);
      fm.hostTax += amt;
      out.hostTax = amt;
    }
    if (h.terms) bumpStat(state, 'concessionTake', (h.extra + h.royalty) * bbl);
  } else if (rid) {
    const wpt = windfallPerBbl(state, fieldId, per) * bbl;
    if (wpt > 0) { spend(state, wpt, 'windfallTax', false); bumpStat(state, 'windfallTaxPaid', wpt); out.windfall = wpt; }
  }
  // prune the ledger (keep ~2 years)
  const keys = Object.keys(E.fiscalMonths);
  if (keys.length > 30) for (const k of keys.sort().slice(0, keys.length - 24)) delete E.fiscalMonths[k];
  return out;
}

const monthKeyOf = (day) => { const { year, month } = dayToDate(day); return `${year}-${String(month).padStart(2, '0')}`; };

// m2/taxes.js: foreign gross and creditable host taxes for a set of month keys.
export function foreignLedger(state, monthKeys) {
  let foreignGross = 0, hostTax = 0;
  for (const k of monthKeys) { const m = state.m3econ?.fiscalMonths?.[k]; if (m) { foreignGross += m.foreignGross; hostTax += m.hostTax; } }
  return { foreignGross, hostTax };
}

// ---- view ---------------------------------------------------------------------------------------------
export function fiscalView(state, fieldId, nbFn = null, opexPerWell = 0) {
  const fdef = fieldDef(fieldId);
  if (!fdef) return null;
  const rid = fdef.regionId;
  const y = yf(state);
  const t = termsFor(state, fieldId);
  const nb = nbFn ? nbFn(state, fieldId) : null;
  const realized = nb?.gross ?? 0;
  const cost = nb ? liftingCostPerBbl(state, fieldId, opexPerWell, nb) : 0;
  const h = t ? hostTaxPerBbl(state, fieldId, realized, cost) : null;
  const nat = natStatus(state, rid);
  const wells = isUsRegion(rid) ? producingWells(state, fieldId).map((w) => ({ id: w.id, name: w.name, rate: round2(w.prod.rate), firstProdYear: wellFirstProdYear(w), tier: usTier(w, y), wptTier: windfallActive(y) ? wellWptTier(state, w) : null })) : [];
  return {
    fieldId, regionId: rid, region: regionDef(rid)?.name ?? rid, year: y,
    terms: t ? { ...t } : null, schedule: (GOVERNMENT_TAKE[rid] ?? []).map(([yy, v]) => ({ from: yy, ...v })),
    posted: h?.posted ?? realized, realized, discount: realized > 0 && h ? 1 - realized / h.posted : 0,
    costPerBbl: cost, royaltyPerBbl: h?.royalty ?? 0, hostTaxPerBbl: h?.extra ?? 0, takePerBbl: h?.take ?? 0,
    takeShare: h && realized - cost > 0 ? h.take / (realized - cost) : 0, keepPerBbl: h ? realized - cost - h.take : null,
    nationalization: { mode: nat.mode, share: nat.share, id: nat.rec?.id ?? null },
    us: isUsRegion(rid) ? { wells, controls: wells.some((w) => w.tier), windfallPerBbl: windfallPerBbl(state, fieldId, realized), independent: isIndependent(state) } : null,
    foreignTaxCredit: !isUsRegion(rid),
    note: t ? (t.royaltyCredited ? 'Fifty-fifty: the royalty counts toward the income tax.' : 'Royalty expensed: royalty plus tax on what is left.') : null,
    fmt: h ? `${fmt$(h.take)}/bbl to the host government` : null,
  };
}

// For tests / UI: per-well nationalization status (participation share by licence year).
export const wellFiscalShare = (state, w) => wellNat(state, w).share;
